import React, { useState, useMemo } from "react";
import { Container, Card } from "react-bootstrap";
import { WithApiAccess } from "../../../lib/HigherOrderComponents/WithApiAccess";
import { withHeaderMessages } from "../../../higherOrderComponents/WithHeaderMessages";
import { LoadingSpinner } from "../../../lib/components/LoadingSpinner";
import { Currency } from "../../../lib/components/Currency";
import "./ContractCommissions.css";
import { SearchForm } from "./SearchForm";
import { AgentGroupCard } from "./AgentGroupCard";
import { BulkActionBar } from "./BulkActionBar";
import { CalculateBonusModal } from "./CalculateBonusModal";
import { groupByAgent } from "./helpers";
import { useContractSearch } from "./hooks/useContractSearch";
import { useStatusUpdate } from "./hooks/useStatusUpdate";
import { useContractSelection } from "./hooks/useContractSelection";
import { CommissionStatus } from "./constants";

const ContractCommissionsApproval = (props) => {
  // `contracts` is the one piece of state genuinely shared across concerns:
  // search writes results into it, status updates patch it optimistically, and
  // selection reads from it. So the container owns it and hands it down.
  const [contracts, setContracts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusCustomers, setBonusCustomers] = useState([]);
  const [isLoadingBonusCustomers, setIsLoadingBonusCustomers] = useState(false);
  const [isCalculatingBonus, setIsCalculatingBonus] = useState(false);

  const { updateStatus, busy, errored, clearErrors } = useStatusUpdate({
    api: props.api,
    setContracts,
  });

  // A fresh fetch clears prior per-row failures.
  const { isLoading, params, searchAndSetHistory } = useContractSearch({
    api: props.api,
    setContracts,
    onResults: clearErrors,
  });

  const {
    selected,
    setSelected,
    confirmDecline,
    setConfirmDecline,
    activeSelection,
    selCount,
    selAmount,
    approvable,
    approveCount,
    blockedCount,
    toggleOne,
    toggleGroup,
    clearSelection,
  } = useContractSelection(contracts);

  const toggleCollapse = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const bulkApprove = async () => {
    setIsSaving(true);
    try {
      const numbers = approvable.map((c) => c.number);
      await updateStatus(numbers, "Approved");
      // Keep any unassigned (un-approvable) selections so the bar shifts to "Decline N".
      setSelected((prev) => {
        const next = new Set(prev);
        numbers.forEach((id) => next.delete(id));
        return next;
      });
    } finally {
      setIsSaving(false);
    }
  };

  const bulkDecline = async () => {
    setIsSaving(true);
    try {
      await updateStatus(
        activeSelection.map((c) => c.number),
        "Declined",
      );
      clearSelection();
    } finally {
      setIsSaving(false);
    }
  };

  const groups = useMemo(() => groupByAgent(contracts), [contracts]);
  const allPending = contracts.filter((c) => c.commissionStatus === CommissionStatus.Pending);

  const loadBonusCustomers = async () => {
    if (isLoadingBonusCustomers || bonusCustomers.length > 0) return;

    setIsLoadingBonusCustomers(true);
    try {
      const response = await props.api.get("/api/customer/profile");
      if (response.status === 200) {
        setBonusCustomers(response.data ?? []);
      }
    } catch {
      props.api.addError("Could not load customers and providers for bonus calculation.");
    } finally {
      setIsLoadingBonusCustomers(false);
    }
  };

  const openCalculateBonus = async () => {
    setShowBonusModal(true);
    await loadBonusCustomers();
  };

  const calculateBonus = async (request) => {
    setIsCalculatingBonus(true);
    try {
      await props.api.post("/api/commission/calculate-bonus", request);
      props.api.addSuccess("Bonus calculation submitted successfully.");
      setShowBonusModal(false);
    } catch {
      props.api.addError("Unable to submit bonus calculation. Please try again.");
    } finally {
      setIsCalculatingBonus(false);
    }
  };

  return (
    <div className="min-vh-100 text-body">
      <Container
        className="py-2 py-sm-3"
        style={{ paddingBottom: selCount > 0 ? "7rem" : undefined }}
      >
        {/* Header */}
        <header className="mb-4">
          <p className="text-secondary mb-0">
            {allPending.length} pending ·{" "}
            <span className="tnum">
              <Currency
                amount={allPending.reduce((s, c) => s + c.netPrice, 0)}
              />
            </span>{" "}
            awaiting review
          </p>
        </header>

        <SearchForm
          params={params}
          onSearch={searchAndSetHistory}
          onOpenCalculateBonus={openCalculateBonus}
        />

        {/* Results */}
        {isLoading ? (
          <LoadingSpinner />
        ) : groups.length === 0 ? (
          <Card className="border-dashed text-center">
            <Card.Body className="py-5">
              <p className="fw-medium mb-1">No contracts match this search</p>
              <p className="text-secondary mb-0">
                Try a different search term, status, or date range.
              </p>
            </Card.Body>
          </Card>
        ) : (
          <div className="d-flex flex-column gap-3">
            {groups.map(([key, rows]) => (
              <AgentGroupCard
                key={key}
                groupKey={key}
                rows={rows}
                selected={selected}
                onToggleOne={toggleOne}
                onToggleGroup={toggleGroup}
                isCollapsed={collapsed.has(key)}
                onToggleCollapse={() => toggleCollapse(key)}
                busy={busy}
                errored={errored}
                onUpdateStatus={updateStatus}
              />
            ))}
          </div>
        )}
      </Container>

      {/* Sticky bulk-action bar */}
      {selCount > 0 && (
        <BulkActionBar
          selCount={selCount}
          selAmount={selAmount}
          blockedCount={blockedCount}
          approveCount={approveCount}
          confirmDecline={confirmDecline}
          isSaving={isSaving}
          onRequestDecline={() => setConfirmDecline(true)}
          onCancelDecline={() => setConfirmDecline(false)}
          onConfirmDecline={bulkDecline}
          onClear={clearSelection}
          onApprove={bulkApprove}
        />
      )}

      <CalculateBonusModal
        show={showBonusModal}
        onHide={() => setShowBonusModal(false)}
        customers={bonusCustomers}
        loading={isLoadingBonusCustomers}
        submitting={isCalculatingBonus}
        onSubmit={calculateBonus}
      />
    </div>
  );
};

export default withHeaderMessages(WithApiAccess(ContractCommissionsApproval));
